import { EmployeeAvatar } from "@/components/employee/EmployeeAvatar.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { GoalCheckInEditModal } from "@/pages/talent/goals/widgets/GoalCheckInEditModal.tsx";
import { useCheckInDelete, useCheckInsSearch } from "@/queries/goals.query.ts";
import {
  ApiGoal,
  ApiGoalCheckIn,
  ApiGoalConfidenceLevel,
  ApiGoalKeyResult,
  ApiGoalMetricType,
  ApiGoalStatus,
  GoalConfidenceLevelItems,
  GoalStatusItems,
} from "@/types/goals.types.ts";
import {
  Button,
  ButtonIcon,
  ConfirmationModal,
  DotSeparator,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  ListItem,
  Skeleton,
  Stack,
  Text,
  Title,
  useModals,
} from "@skailer-com/uikit";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

interface GoalUpdateItemProps {
  checkIn: ApiGoalCheckIn;
  canEdit?: boolean;
  onEdit: (checkIn: ApiGoalCheckIn) => void;
  onDelete: (checkInId: number) => void;
}

function GoalUpdateItem({ checkIn, canEdit, onEdit, onDelete }: GoalUpdateItemProps) {
  const { t } = useTranslation();
  const { renderTimeAgo, renderEmployeeName, renderCurrency } = useFormatter();
  const [expanded, setExpanded] = useState<boolean>(false);

  const confidenceLevelItems = GoalConfidenceLevelItems();
  const statusItems = GoalStatusItems();

  const renderConfidenceLevelText = (level: ApiGoalConfidenceLevel) => {
    const levelItem = confidenceLevelItems.find((item) => item.id === level);
    return levelItem?.title ?? "";
  };

  const renderStatusText = (status: ApiGoalStatus) => {
    const statusItem = statusItems.find((item) => item.id === status);
    return statusItem?.title ?? "";
  };

  const renderKeyResultValue = (
    kr: ApiGoalKeyResult,
    value: ApiGoalKeyResult["previous_value"]
  ) => {
    if (!value) return "-";
    switch (kr.metric_type) {
      case ApiGoalMetricType.boolean:
        return value.value_bool ? t("common:yes") : t("common:no");
      case ApiGoalMetricType.percentage:
        return `${value.value_float ?? 0}%`;
      case ApiGoalMetricType.currency:
        return value.value_float && value.value_text
          ? renderCurrency(`${value.value_float}`, value.value_text)
          : "-";
      case ApiGoalMetricType.number:
      default:
        return value.value_float ?? 0;
    }
  };

  const hasStatusChange = checkIn.previous_status !== checkIn.current_status;
  const hasConfidenceChange =
    checkIn.previous_confidence_level !== checkIn.current_confidence_level;
  const hasKeyResults = checkIn.key_results && checkIn.key_results.length > 0;
  const hasExpandableContent = hasStatusChange || hasConfidenceChange || hasKeyResults;

  return (
    <Stack className={"bg-dark/5 border border-dark/5 rounded-xs p-4"} gap={"sm"}>
      <Stack direction={"horizontal"} gap={"md"} justify={"between"} items={"center"}>
        <Stack gap={"sm"}>
          <Stack direction={"horizontal"} items={"center"} gap={"sm"} justify={"between"}>
            {checkIn.author && <EmployeeAvatar size={"sm"} employee={checkIn.author} />}
            <DotSeparator className={"text-dark/60 flex-1"}>
              <Text>
                {checkIn?.author ? renderEmployeeName(checkIn?.author) : t("common:unknown")}
              </Text>
              <Text>{checkIn ? renderTimeAgo(checkIn.datetime_adjustment) : ""}</Text>
            </DotSeparator>
            {canEdit && (
              <DropdownMenu
                trigger={<FontAwesomeIcon icon={"fa-light fa-ellipsis cursor-pointer"} />}
              >
                <DropdownMenuItem
                  leftIcon={<FontAwesomeIcon icon="fa-light fa-pen" />}
                  onClick={() => onEdit(checkIn)}
                  title={t("common:edit")}
                />
                <DropdownMenuItem
                  variant={"menuDanger"}
                  leftIcon={<FontAwesomeIcon icon="fa-light fa-trash" />}
                  onClick={() => onDelete(checkIn.result_session_id)}
                  title={t("common:delete") + "..."}
                />
              </DropdownMenu>
            )}
          </Stack>

          <Text className="text-dark/80 break-word">
            {checkIn.comment?.text || t("goals:goal_updated")}
          </Text>
        </Stack>
        {hasExpandableContent && (
          <ButtonIcon
            icon={
              <FontAwesomeIcon
                icon={`fa-light ${!expanded ? "fa-angle-right" : "fa-angle-down"}`}
              />
            }
            variant={"plain"}
            onClick={() => setExpanded(!expanded)}
          />
        )}
      </Stack>

      {expanded && (
        <Stack>
          {hasStatusChange && (
            <ListItem
              preTitle={t("common:status")}
              captionSlot={
                <Stack direction={"horizontal"} gap={"xs"} items={"center"}>
                  <Text>{renderStatusText(checkIn.previous_status)} →</Text>
                  <Text className="font-semibold">{renderStatusText(checkIn.current_status)}</Text>
                </Stack>
              }
            />
          )}
          {hasConfidenceChange && (
            <ListItem
              preTitle={t("goals:confidence_level")}
              captionSlot={
                <Stack direction={"horizontal"} gap={"xs"} items={"center"}>
                  <Text>{renderConfidenceLevelText(checkIn.previous_confidence_level)} →</Text>
                  <Text className="font-semibold">
                    {renderConfidenceLevelText(checkIn.current_confidence_level)}
                  </Text>
                </Stack>
              }
            />
          )}
          {hasKeyResults && (
            <>
              {checkIn.key_results.map((kr, index) => (
                <ListItem
                  key={kr.id}
                  preTitle={kr.title}
                  className={index === checkIn.key_results.length - 1 ? "border-b-0" : ""}
                  captionSlot={
                    <Stack direction={"horizontal"} gap={"xs"} items={"center"}>
                      <Text>{renderKeyResultValue(kr, kr.previous_value)} →</Text>
                      <Text className="font-semibold">
                        {renderKeyResultValue(kr, kr.current_value)}
                      </Text>
                    </Stack>
                  }
                />
              ))}
            </>
          )}
        </Stack>
      )}
    </Stack>
  );
}

interface GoalUpdatesProps {
  goal?: ApiGoal;
  goalId: number;
}

export function GoalUpdates({ goalId, goal }: GoalUpdatesProps) {
  const { t } = useTranslation();
  const { openModal } = useModals();
  const checkInDelete = useCheckInDelete();

  const [checkInsPage, setCheckInsPage] = useState(1);
  const [allCheckIns, setAllCheckIns] = useState<ApiGoalCheckIn[]>([]);
  const [showAllCheckIns, setShowAllCheckIns] = useState(false);

  const { data: checkInsData, isLoading: isCheckInsLoading } = useCheckInsSearch(
    goalId,
    checkInsPage,
    true
  );

  const [syncedCheckInsData, setSyncedCheckInsData] = useState<typeof checkInsData>(undefined);
  const [syncedCheckInsPage, setSyncedCheckInsPage] = useState(checkInsPage);
  if (checkInsData !== syncedCheckInsData || checkInsPage !== syncedCheckInsPage) {
    setSyncedCheckInsData(checkInsData);
    setSyncedCheckInsPage(checkInsPage);
    if (checkInsData?.items) {
      if (checkInsPage === 1) {
        setAllCheckIns(checkInsData.items);
      } else {
        setAllCheckIns((prev) => [...prev, ...checkInsData.items]);
      }
    }
  }

  const hasMoreCheckIns = checkInsData
    ? checkInsData.metadata.total_items > allCheckIns.length
    : false;

  const remainingCheckInsCount = !showAllCheckIns
    ? allCheckIns.length - 1
    : checkInsData
      ? checkInsData.metadata.total_items - allCheckIns.length
      : 0;

  const handleLoadMoreCheckIns = useCallback(() => {
    if (!showAllCheckIns) {
      setShowAllCheckIns(true);
    } else if (hasMoreCheckIns) {
      setCheckInsPage((prev) => prev + 1);
    }
  }, [showAllCheckIns, hasMoreCheckIns]);

  const visibleCheckIns = showAllCheckIns ? allCheckIns : allCheckIns.slice(0, 1);

  const handleDeleteCheckIn = (checkInId: number) => {
    openModal(ConfirmationModal, {
      question: t("goals:delete_update"),
      text: t("goals:delete_update_confirm"),
      acceptTitle: t("common:delete"),
      onAccept: async () => {
        await checkInDelete.mutateAsync({ goalId, checkInId });
      },
    });
  };

  const handleEditCheckIn = (checkIn: ApiGoalCheckIn) => {
    openModal(GoalCheckInEditModal, {
      goalId,
      existingCheckIn: checkIn,
    });
  };

  return (
    <>
      {isCheckInsLoading && allCheckIns.length === 0 ? (
        <Skeleton className="h-20 w-full mt-6" />
      ) : visibleCheckIns.length > 0 ? (
        <>
          <Title size={"xs"} header={t("goals:recent_updates")} paddingBottom paddingTop />
          <Stack gap={"md"}>
            <Stack gap={"sm"}>
              {visibleCheckIns.map((checkIn) => (
                <GoalUpdateItem
                  key={checkIn.result_session_id}
                  checkIn={checkIn}
                  canEdit={goal?.acl.can_edit}
                  onEdit={handleEditCheckIn}
                  onDelete={handleDeleteCheckIn}
                />
              ))}
            </Stack>

            <Stack direction={"horizontal"} gap={"sm"}>
              {((!showAllCheckIns && allCheckIns.length > 1) || hasMoreCheckIns) && (
                <Button
                  variant={"secondary"}
                  onClick={handleLoadMoreCheckIns}
                  isLoading={isCheckInsLoading}
                >
                  {t("common:show_more_template", { count: remainingCheckInsCount })}
                </Button>
              )}
            </Stack>
          </Stack>
        </>
      ) : null}
    </>
  );
}
